import {
  Box,
  Button,
  Container,
  ContainerProps,
  Flex,
  Heading,
  IncrementInput,
  LinkButton,
  Price,
  Skeleton,
  Stack,
  StarRating,
  Text,
  Title,
  useToast,
  VisuallyHidden,
  Divider,
} from '@thirstycamel/ui'
import type { CoreProductEntity } from '@ts/core/src/modules/coreproduct/coreproduct.entity'
import React, { memo, useCallback, useEffect, useState, Fragment } from 'react'
import { useUser } from '../../hooks/useUser'
import { useActions, useStore } from '../../store/hooks'
import { getNiceErrorMessage } from '../../utils/getNiceErrorMessage'
import ProductAvailability from '../ProductAvailability'
import ProductBadge from '../ProductBadge'
import ProductFavouriteStatus from '../ProductFavouriteStatus'
import { ProductBundleOverviewGroup } from '../ProductBundleOverviewGroup'
import ProductSource from '../ProductSource'
import ProductImagePreview from '../ProductImagePreview'

export interface ProductBundleOverviewProps extends ContainerProps {
  product: CoreProductEntity
  isFetching?: boolean
}

export const ProductBundleOverview = ({
  product,
  isFetching,
  ...restProps
}: ProductBundleOverviewProps) => {
  const { isLoggedIn } = useUser()
  const toast = useToast()

  const selectedStore = useStore(s => s.store.selectedStore)
  const cart = useStore(s => s.cart)
  const { addProductToCart, createCart } = useActions(s => s.cart)
  const { openPopout } = useActions(store => store.store)
  const location = useStore(s => s.location)

  const [quantity, setQuantity] = useState(1)
  const [isAddingToCart, setAddingToCart] = useState(false)
  const [adjustedPrice, setAdjustedPrice] = useState(product?.pricing?.price)
  const [adjustedOfferPrice, setAdjustedOfferPrice] = useState(product?.pricing?.price)
  const [adjustedSpecialPrice, setAdjustedSpecialPrice] = useState(product?.specialPricing?.price)

  const region = selectedStore?.region || location.region || 'VIC'
  const isCamelCard = region === 'SA' || region === 'NT'
  const specialPrice = product?.specialPricing?.price

  const bundlePrice: number = adjustedOfferPrice || adjustedSpecialPrice || adjustedPrice

  useEffect(() => {
    if (product?.pricing) {
      setAdjustedPrice(quantity * product?.pricing?.price)
    }

    if (product?.offerPrice) {
      setAdjustedOfferPrice(quantity * product?.offerPrice)
    }

    if (product?.specialPricing) {
      setAdjustedSpecialPrice(quantity * product?.specialPricing?.price)
    }
  }, [quantity, product])

  const handleAddToCart = useCallback(async () => {
    /* Prevent double-clicks */
    if (cart.isFetching) return

    setAddingToCart(true)

    /* If the user doesn't have a cart, create one for them. */
    if (!cart.cartId) await createCart()

    try {
      await addProductToCart({ product, qty: quantity })
    } catch (error) {
      toast({
        title: getNiceErrorMessage(error),
        status: 'error',
      })
    } finally {
      setAddingToCart(false)
    }
  }, [addProductToCart, product, cart.cartId, quantity, createCart])

  return (
    <Container pt={[6, , 10]} pb={[5, 5, 20]} size="xs" {...restProps}>
      <Stack spacing={8} w="100%">
        <Flex
          direction={['column', , 'row']}
          justify="center"
          alignItems={['center', , 'flex-start']}
        >
          <Box
            width={[, , '50%']}
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            mr={[, 4, 4, 10]}
            ml={[, 0, 0]}
            position="relative"
            textAlign="center"
            minWidth={[0, '50%', '50%', '50%']}
          >
            {/* {showEveryDayValuePrice ? (
              <ProductBadge type="everyday" />
            ) : isSpecial ? (
              <ProductBadge type="save" price={product?.pricing?.price - specialPrice} />
            ) : showMemberPrice ? (
              <ProductBadge type="member" />
            ) : hasCustomBadge ? (
              <ProductBadge type="image" src={product?.badge?.image.filename} />
            ) : shouldShow1kDayBadge(product) ? (
              <Box position="absolute" top={1} left={1} color="white" userSelect="none">
                <Image src={`/static/images/win1kaday.png`} width="100px" />
              </Box>
            ) : isNew && isNewActive ? (
              <ProductBadge type="new" />
            ) : isBundle ? (
              <ProductBadge type="bundle" />
            ) : null} */}
            <ProductImagePreview
              images={product?.media}
              hideCarousel={true}
              isFetching={isFetching}
            />
          </Box>
          <Stack spacing={8} w="100%">
            <Stack spacing={3} w="100%">
              <Flex justifyContent="space-between">
                <Stack spacing={1} maxWidth="320px">
                  <Heading
                    fontSize={['xl', , 'lg']}
                    color="pink.500"
                    textTransform="uppercase"
                    aria-hidden
                  >
                    {product?.bundle?.isMultibuy
                      ? product.bundle.multibuyLabel || 'Multibuy'
                      : 'Bundle'}
                  </Heading>

                  <Title fontSize="md" textTransform="uppercase">
                    {product?.bundle?.title ?? <Skeleton h="2rem" w="12rem" />}
                  </Title>
                </Stack>

                {product?.id && <ProductFavouriteStatus productId={product.id} />}
              </Flex>

              {!product ? (
                <Skeleton h="1.5rem" w="8rem" />
              ) : (
                <Stack spacing={2} isInline alignItems="center">
                  <StarRating value={product?.productGroup?.rating || 0} />

                  <Text fontWeight="bold" color="blackAlpha.600" fontSize="sm">
                    ({product?.productGroup?.numberOfReviews || 0})
                  </Text>
                </Stack>
              )}
            </Stack>

            <Divider />

            {(product?.pricing || product?.specialPricing) && (
              <Stack>
                {product.bundle.groups.map((group, index) => {
                  return (
                    <Heading key={group?.heading} color="pink.500" textTransform="uppercase">
                      {group.heading}
                    </Heading>
                  )
                })}

                {product?.offerPrice && (
                  <Stack>
                    <Box backgroundColor="gray.50" borderColor="gray.200" borderWidth="2px" p={5}>
                      <Flex
                        justifyContent="space-between"
                        flexDirection={['column', , 'row']}
                        alignItems="center"
                        mb={5}
                      >
                        <Flex alignItems="center">
                          <Heading as={Price} color={'pink.500'} fontSize={['4xl', , '3xl']} mr={2}>
                            {adjustedOfferPrice}
                          </Heading>
                          <ProductBadge ml={4} type="member" position="relative" top={0} left={0} />
                        </Flex>

                        {!isLoggedIn && (
                          <Stack mt={[5, , 0]} width={['100%', , 'auto']}>
                            <LinkButton
                              variantColor="pink"
                              href={isCamelCard ? '/page/[slug]' : '/register'}
                              hrefAs={isCamelCard ? '/page/camel-card' : null}
                            >
                              JOIN FOR FREE
                            </LinkButton>
                          </Stack>
                        )}
                      </Flex>

                      <Text fontSize="sm" color="gray.500">
                        Member pricing is applied at checkout and may be subject to offer specific
                        redemption terms and conditions. Member pricing is only available to Hump
                        Club loyalty members.
                      </Text>
                    </Box>

                    <Heading fontSize="xs" color="gray.600" mt={5}>
                      NON MEMBER PRICE
                    </Heading>
                  </Stack>
                )}

                <Stack>
                  <Flex align="center">
                    {product?.pricing && !product?.specialPricing && (
                      <Heading
                        as={Price}
                        color={specialPrice ? 'blackAlpha.500' : 'pink.500'}
                        textDecoration={specialPrice ? 'line-through' : ''}
                        fontSize={product?.offerPrice ? 'md' : ['4xl', , '3xl']}
                        mr={2}
                      >
                        {adjustedPrice}
                      </Heading>
                    )}

                    {specialPrice && (
                      <Heading
                        fontSize={product?.offerPrice ? 'md' : ['4xl', , '3xl']}
                        as={Price}
                        color={product?.offerPrice ? 'gray.500' : 'pink.500'}
                      >
                        {adjustedSpecialPrice}
                      </Heading>
                    )}
                  </Flex>

                  <Text fontSize="sm" color="gray.500">
                    *prices may vary in {region === 'QLD' ? 'store' : 'regional areas'}
                  </Text>
                </Stack>
              </Stack>
            )}

            {(product?.pricing || product?.specialPricing) && <Divider />}

            <Text>
              {product ? (
                product.bundle.description || 'This product has no description.'
              ) : (
                <Stack w="100%">
                  <Skeleton height="1rem" width="100%" />
                  <Skeleton height="1rem" width="100%" />
                  <Skeleton height="1rem" width="100%" />
                  <Skeleton height="1rem" width="100%" />
                  <Skeleton height="1rem" width="25%" />
                </Stack>
              )}
            </Text>

            {(product?.pricing != null || product?.specialPricing != null) &&
              !selectedStore?.externalURL &&
              (selectedStore?.isPickupReady || selectedStore?.isDeliveryReady) && (
                <Box>
                  <Button
                    onClick={handleAddToCart}
                    variantColor="pink"
                    loadingText="Loading"
                    width={['100%', 'auto']}
                    isLoading={isFetching || isAddingToCart}
                    isDisabled={!selectedStore}
                  >
                    Add To Cart
                  </Button>
                </Box>
              )}

            {selectedStore?.externalURL && (
              <LinkButton
                mt={[5, 5, 0]}
                variantColor="pink"
                width={['100%', 'auto']}
                isExternal
                href={`${selectedStore.externalURL}${
                  product?.santExternalID ? `/lines/${product.santExternalID}` : ''
                }`}
                isLoading={cart.isFetching}
              >
                Shop now
              </LinkButton>
            )}
          </Stack>
        </Flex>

        <Divider />

        <Box>
          <ProductSource product={product} />
        </Box>

        <Divider />

        {product.bundle?.groups?.length > 0 && (
          <Box
            borderWidth="1px"
            borderColor="gray.200"
            bg="gray.50"
            px={[3, 5]}
            py={5}
            borderRadius="md"
          >
            <Heading>Included:</Heading>

            <Divider mt={5} />

            {product.bundle.groups.map((group, index) => {
              return (
                <Fragment key={group.id}>
                  {index > 0 && <Divider />}
                  <ProductBundleOverviewGroup group={group} price={bundlePrice} />
                </Fragment>
              )
            })}
          </Box>
        )}
      </Stack>
    </Container>
  )
}

export default memo(ProductBundleOverview)
